/**
 * Presets.tsx — trainer protocol preset editor (FEA-026 / P8-3, 2026-05-09).
 *
 * Path: /dashboard/presets — RequireAuth trainer / admin
 *
 * Lists trainer's own protocol presets; create / edit / delete inline.
 * Used by Studio right rail: when trainer enters live phase the rail
 * surfaces these as one-click load options alongside the four built-ins
 * (alpha-uplift / theta-beta / hrv-breath / custom).
 *
 * Schema: protocol_presets (D1 migration 0003).
 *   { id, name, source, game, params, notes }
 *   params is { T_a, T_b, T_h, eps } — same shape applyPreset fans out as
 *   param_update events.
 */
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiException } from "../lib/api.js";

export interface ProtocolPreset {
  id: string;
  trainer_id: string;
  name: string;
  source: string | null;
  game: string | null;
  params: Record<string, unknown>;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const SOURCE_OPTIONS = [
  { v: "",         label: "(自動 / 監測)" },
  { v: "eeg",      label: "eeg · 8ch 1000 SPS" },
  { v: "hrv",      label: "hrv · rPPG 30 fps" },
  { v: "baseline", label: "baseline · 純錄無 game" },
  { v: "behavior", label: "behavior · task accuracy/RT" },
  { v: "none",     label: "none · metric-only" },
];
const GAME_OPTIONS = [
  { v: "",                     label: "(無遊戲 — 監測模式)" },
  { v: "soramynd-plane",       label: "SoraMynd · 紙飛機 (alpha)" },
  { v: "soramynd-baseball",    label: "SoraMynd · 棒球 (θ/β)" },
  { v: "soramynd-zentangle",   label: "SoraMynd · Zentangle" },
  { v: "soramynd-karesansui",  label: "SoraMynd · 枯山水" },
  { v: "visiomynd-breath",     label: "VisioMynd · 呼吸調節" },
  { v: "myndscape-task",       label: "MyndScape · 認知作業" },
];

const PARAM_DEFS = [
  { k: "T_a", label: "T_a · alpha µV²", min: 0, max: 30,  step: 0.5,  decimals: 1, fallback: 12   },
  { k: "T_b", label: "T_b · θ/β ratio", min: 0, max: 5,   step: 0.1,  decimals: 2, fallback: 2.5  },
  { k: "T_h", label: "T_h · rmssd ms",  min: 0, max: 120, step: 1,    decimals: 0, fallback: 40   },
  { k: "eps", label: "ε · smoothing",   min: 0, max: 1,   step: 0.05, decimals: 2, fallback: 0.30 },
] as const;

export default function Presets() {
  const [presets, setPresets] = useState<ProtocolPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ProtocolPreset | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  async function load() {
    try {
      const r = await api<{ ok: true; presets: ProtocolPreset[] }>("/api/presets");
      setPresets(r.presets);
    } catch (e) {
      setError(e instanceof ApiException ? e.body.error : String(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function remove(id: string) {
    if (!confirm("確定刪除這組 preset？")) return;
    try {
      await api(`/api/presets/${id}`, { method: "DELETE" });
      setPresets(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      alert("刪除失敗：" + (e instanceof ApiException ? e.body.error : String(e)));
    }
  }

  return (
    <main className="page">
      <header className="mast">
        <div className="row" style={{ gap: "var(--s-5)" }}>
          <div className="mast__brand"><span>CogniHome</span><span className="slash">/</span><span style={{ color: "var(--ink-soft)" }}>Protocol presets</span></div>
        </div>
        <nav className="mast__nav">
          <Link to="/dashboard">← 工作台</Link>
        </nav>
      </header>

      <section style={{ padding: "var(--s-5) var(--s-6)", maxWidth: 880, margin: "0 auto" }}>
        <div className="row row--between" style={{ marginBottom: "var(--s-4)" }}>
          <div>
            <div className="eyebrow">Presets</div>
            <h2 style={{ marginTop: 4 }}>我的 protocol presets</h2>
            <p className="muted" style={{ fontSize: "var(--t-sm)", maxWidth: 520, marginTop: "var(--s-2)", lineHeight: 1.6 }}>
              在 Studio 右欄一鍵載入：source + game + thresholds 一次設好。內建的四個 preset
              （alpha-uplift / theta-beta / hrv-breath / custom）保留；這裡是你自己加的。
            </p>
          </div>
          <button className="btn btn--rust" onClick={() => setShowNew(true)}>＋ 新增 preset</button>
        </div>

        {error && <div className="alert" style={{ marginBottom: "var(--s-4)" }}>{error}</div>}

        {loading ? (
          <div className="muted">載入中…</div>
        ) : presets.length === 0 ? (
          <div className="empty">
            <div className="italic-display" style={{ fontSize: "var(--t-xl)", marginBottom: "var(--s-2)" }}>還沒有自訂 preset</div>
            <div className="muted" style={{ marginBottom: "var(--s-4)" }}>建第一組節省 Studio 中的設定步驟</div>
            <button className="btn btn--rust" onClick={() => setShowNew(true)}>＋ 新增 preset</button>
          </div>
        ) : (
          <div className="list">
            <div className="list-row" style={{ borderBottom: "1px solid var(--ink)", paddingBottom: "var(--s-2)", marginBottom: "var(--s-1)" }}>
              <div className="serial">名稱 / 內容</div>
              <div className="serial">Thresholds</div>
              <div className="serial">更新</div>
              <div className="serial" style={{ textAlign: "right" }}>動作</div>
            </div>
            {presets.map(p => (
              <div key={p.id} className="list-row">
                <div>
                  <div className="list-row__name">{p.name}</div>
                  <div className="list-row__meta">
                    {p.source ?? "—"} · {p.game ?? "(無遊戲)"}
                  </div>
                </div>
                <div className="muted tabular" style={{ fontSize: "var(--t-xs)", lineHeight: 1.5 }}>
                  {PARAM_DEFS.map(d => {
                    const v = (p.params[d.k] as number | undefined) ?? d.fallback;
                    return <div key={d.k}>{d.k}: {Number(v).toFixed(d.decimals)}</div>;
                  })}
                </div>
                <div className="muted tabular">{p.updated_at.slice(0, 10)}</div>
                <div className="row" style={{ justifyContent: "flex-end", gap: 6 }}>
                  <button className="btn btn--ghost btn--small" onClick={() => setEditing(p)}>編輯</button>
                  <button className="btn btn--ghost btn--small" onClick={() => remove(p.id)} style={{ color: "var(--danger)", borderColor: "var(--danger)" }}>刪</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {(showNew || editing) && (
        <PresetEditor
          preset={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </main>
  );
}

function PresetEditor({ preset, onClose, onSaved }: {
  preset: ProtocolPreset | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!preset;
  const initial = useMemo(() => ({
    name:   preset?.name   ?? "",
    source: preset?.source ?? "",
    game:   preset?.game   ?? "",
    notes:  preset?.notes  ?? "",
    params: PARAM_DEFS.reduce<Record<string, number>>((acc, d) => {
      acc[d.k] = (preset?.params[d.k] as number | undefined) ?? d.fallback;
      return acc;
    }, {}),
  }), [preset]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const body = {
        name:   form.name,
        source: form.source || null,
        game:   form.game   || null,
        params: form.params,
        notes:  form.notes  || null,
      };
      if (isEdit && preset) {
        await api(`/api/presets/${preset.id}`, { method: "PUT", body });
      } else {
        await api(`/api/presets`, { method: "POST", body });
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiException ? e.body.error : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onSubmit={submit} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="serial" style={{ marginBottom: "var(--s-2)" }}>{isEdit ? "Edit preset" : "New preset"}</div>
        <h3 className="modal__title">{isEdit ? "編輯 preset" : "新增 protocol preset"}</h3>

        <label className="field">
          <span>名稱</span>
          <input type="text" required maxLength={80} value={form.name}
                 onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                 placeholder="如：高 alpha 訓練" />
        </label>

        <div className="row" style={{ gap: "var(--s-3)" }}>
          <label className="field" style={{ flex: 1 }}>
            <span>Source</span>
            <select value={form.source} onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}>
              {SOURCE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span>Game</span>
            <select value={form.game} onChange={(e) => setForm(f => ({ ...f, game: e.target.value }))}>
              {GAME_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </label>
        </div>

        <div style={{ marginTop: "var(--s-3)" }}>
          <div className="serial" style={{ marginBottom: 4 }}>Thresholds (T_a / T_b / T_h / ε)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-3)" }}>
            {PARAM_DEFS.map(d => (
              <label key={d.k} className="field" style={{ marginTop: 0 }}>
                <span style={{ fontSize: "var(--t-xs)" }}>{d.label}</span>
                <input type="number" step={d.step} min={d.min} max={d.max}
                       value={form.params[d.k]}
                       onChange={(e) => setForm(f => ({
                         ...f, params: { ...f.params, [d.k]: parseFloat(e.target.value) },
                       }))} />
              </label>
            ))}
          </div>
        </div>

        <label className="field">
          <span>備註（給自己看的，個案不會看到）</span>
          <textarea rows={3} value={form.notes}
                    onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
        </label>

        {err && <div className="alert" style={{ marginTop: "var(--s-2)" }}>{err}</div>}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>取消</button>
          <button type="submit" className="btn btn--rust" disabled={busy}>
            {busy ? "儲存中…" : isEdit ? "儲存變更" : "建立"}
          </button>
        </div>
      </form>
    </div>
  );
}
